/* eslint-disable no-console */
import axios from 'axios';
import _ from 'lodash';
import { createSelector } from 'reselect';
import { v4 as uuidv4 } from 'uuid';

import { logoutRequest } from '../../actions/AuthActions';
import { expireToken } from '../../actions/AuthActions/ExpireToken';
import { restoreTitle } from '../../actions/AuthActions/RestoreTitle';
import { handleErrors } from '../../actions/ErrorActions';
import {
  setCustomLongLoadPageMessage,
  setShowLongLoadPageMessage,
} from '../../actions/LongLoadPageActions';
import { setFinishSubmit } from '../../actions/WorkflowActions';
import {
  DEFAULT_FILE_SIZE_UPLOAD_MESSAGE,
  ENDPOINTS_FILE_UPLOAD,
  ENDPOINTS_TO_SHOW_LONG_LOAD_API_MESSAGE,
  ENDPOINTS_WITH_CUSTOM_LONG_LOAD_MESSAGE,
  FILE_SIZE_UPLOAD_ERROR_TRANSLATION_KEY,
  LONG_LOAD_API_TIME,
} from '../../constants/Common';
import {
  setDateAddBusinessDays,
  setModuleIsLicensed,
  setNumberingScheme,
} from '../../form-designer/actions/runtime.actions';
import { translate } from '../../form-designer/utils/form-manager.utils';
import { selectSessionExpirationErrorCode } from '../../selectors/AuthSelectors';
import { store } from '../../store';
import { getEnvVar } from '../../utils/env';
import { hasValue } from '../../utils/fns';
import { isNilOrEmpty } from '../../utils/ramdaUtils';

const adminAppUrl = getEnvVar('REACT_APP_ADMIN_APP_URL');
export let pendingRequests = 0;
const inProgressApiTimeoutIds = {};

const stopInProgressApiTimeout = (apiId, apiUrl) => {
  clearTimeout(inProgressApiTimeoutIds[apiId]);
  delete inProgressApiTimeoutIds[apiId];

  const isLongLoadMessageShown = store.getState().longLoadPageMessage.showMessage;
  const hasCustomMessage = store.getState().longLoadPageMessage.customMessage;
  const hasInProgressApis = Object.keys(inProgressApiTimeoutIds).length;

  if (ENDPOINTS_WITH_CUSTOM_LONG_LOAD_MESSAGE.includes(apiUrl) && hasCustomMessage) {
    store.dispatch(setCustomLongLoadPageMessage(''));
  }

  //close long load banner when all the observable apis are finished
  if (!hasInProgressApis && isLongLoadMessageShown) {
    store.dispatch(setShowLongLoadPageMessage(false));
  }
};

const getActorCodeWithDomain = () =>
  store.getState().auth.actorCodeWithDomain ?? localStorage.getItem('actorCodeWithDomain');

const hasLogonUserChanged = (prevActorCodeWithDomain) =>
  hasValue(prevActorCodeWithDomain) && getActorCodeWithDomain() !== prevActorCodeWithDomain;

const smartSolveApi = axios.create({
  baseURL: `${adminAppUrl}/apis/`,
});

const waitLoginSuccess = () => (dispatch, getState) => {
  return new Promise((resolve) => {
    const unsubscribe = store.subscribe(() => {
      const state = getState();
      if (!state.auth.waitForLoginSuccess) {
        unsubscribe();
        resolve();
      }
    });
  });
};

async function getErrors({ data, config, headers }) {
  if (config.responseType === 'blob') {
    const contentType = headers['content-type'];
    if (contentType === 'application/json') {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('loadend', () => {
          const { errors = [] } = JSON.parse(reader.result);
          resolve(errors);
        });
        reader.readAsText(data);
      });
    }
  }

  return data.errors || [];
}

function handleInterruptedSubmitCall(res) {
  if (res.config.url.endsWith('authentication') || res.config.url.endsWith('submitworkflowdata')) {
    const { data } = res;

    if (!data.hasError && !data.SubmitWorkflowResult?.hasError) {
      store.dispatch(setFinishSubmit(true));
    }
  }
  return res;
}

function getError(errorMessage, errorNumber, hasSessionExpired) {
  let code = errorNumber;
  let message = errorMessage;

  if (hasSessionExpired) {
    const { sessionExpirationErrorCode, sessionExpirationErrorMessage } = store.getState().auth;

    const { translations } = store.getState();

    const translatedMessage = translate(translations, sessionExpirationErrorCode);

    code = sessionExpirationErrorCode;
    message =
      translatedMessage === sessionExpirationErrorCode
        ? sessionExpirationErrorMessage
        : translatedMessage;
  }

  return { message, code };
}

const refreshAuthorizationToken = async (response) => {
  pendingRequests -= 1;

  const apiUrl = response.config.url.toLowerCase();

  if (ENDPOINTS_TO_SHOW_LONG_LOAD_API_MESSAGE.includes(apiUrl)) {
    stopInProgressApiTimeout(response.config.requestId, apiUrl);
  }

  const errors = await getErrors(response);

  if (!errors.length) {
    return response;
  }

  const { errorMessage, errorNumber } = _.head(errors);
  const sessionExpirationErrorCode = selectSessionExpirationErrorCode(store.getState());
  const hasSessionExpired =
    errorNumber === sessionExpirationErrorCode && sessionExpirationErrorCode !== '';

  if (!(hasSessionExpired || errorNumber === '50001')) {
    return response;
  }

  const cancelToken = new Promise(() => {});
  const { message, code } = getError(errorMessage, errorNumber, hasSessionExpired);

  store.dispatch(expireToken(message, code));

  if (code === '50001') {
    store.dispatch(logoutRequest(true));

    return cancelToken;
  }

  const prevActorCodeWithDomain = localStorage.getItem('prevActorCodeWithDomain');

  await store.dispatch(waitLoginSuccess());

  store.dispatch(restoreTitle());

  return hasLogonUserChanged(prevActorCodeWithDomain)
    ? cancelToken
    : smartSolveApi(response.config).then(handleInterruptedSubmitCall);
};

const setAuthorizationToken = (config) => {
  pendingRequests += 1;

  if (/\/(getLoginMethod|login|password\/change)$/.test(config.url)) {
    return config;
  }

  const adminAppUrl = localStorage.getItem('adminAppUrl');
  const cancelToken = new Promise(() => {});

  if (!config.baseURL.includes(adminAppUrl)) {
    store.dispatch(logoutRequest(true));

    return cancelToken;
  }

  const apiUrl = config.url.toLowerCase();

  if (ENDPOINTS_TO_SHOW_LONG_LOAD_API_MESSAGE.includes(apiUrl)) {
    const timer = setTimeout(() => {
      const isLongLoadMessageShown = store.getState().longLoadPageMessage.showMessage;

      if (!isLongLoadMessageShown) {
        store.dispatch(setShowLongLoadPageMessage(true));
      }
    }, LONG_LOAD_API_TIME);

    const requestId = uuidv4();
    config.requestId = requestId;

    inProgressApiTimeoutIds[requestId] = timer;
  }

  if (ENDPOINTS_FILE_UPLOAD.includes(apiUrl)) {
    const { translations } = store.getState();
    const { data } = config;

    if (data instanceof FormData) {
      const maxAllowedContentLength = +localStorage.getItem('maxAllowedContentLength');
      const translatedUploadMessage = translate(
        translations,
        FILE_SIZE_UPLOAD_ERROR_TRANSLATION_KEY
      );

      const errorMessage = (translatedUploadMessage === FILE_SIZE_UPLOAD_ERROR_TRANSLATION_KEY
        ? DEFAULT_FILE_SIZE_UPLOAD_MESSAGE
        : translatedUploadMessage
      ).replace('{0}', (maxAllowedContentLength / (1024 * 1024)).toFixed(2));

      const isFileTooLarge = (value, maxAllowedContentLength) =>
        value instanceof File && value.size > maxAllowedContentLength;

      for (const [, value] of data.entries()) {
        if (isFileTooLarge(value, maxAllowedContentLength)) {
          return Promise.reject({
            fileUploadError: true,
            errorResponse: {
              isTopBanner: true,
              hasError: true,
              errors: [
                {
                  errorMessage,
                },
              ],
            },
          });
        }
      }
    }
  }

  const { auth } = store.getState();

  if (!isNilOrEmpty(auth.token)) {
    config.headers.Authorization = `Bearer ${auth.token}`;
  }

  return config;
};

const handleCrashedErrors = (error) => {
  console.log('Error response: ', error.message);

  if (error.fileUploadError) {
    return Promise.resolve({
      data: error.errorResponse,
    });
  }

  const apiUrl = error.config.url.toLowerCase();

  if (ENDPOINTS_TO_SHOW_LONG_LOAD_API_MESSAGE.includes(apiUrl)) {
    stopInProgressApiTimeout(error.config.requestId, apiUrl);
  }

  const { translations } = store.getState();

  //TODO: get a key for translation after it will be included for this case
  const customError = {
    message: translate(
      translations,
      'One or more error has been encountered with the application server. Please contact system administrator.'
    ),
  };

  const fullAdditionalProps = {
    isTopBanner: true,
  };

  store.dispatch(handleErrors(customError, fullAdditionalProps));

  return;
};

smartSolveApi.interceptors.request.use(setAuthorizationToken);
smartSolveApi.interceptors.response.use(refreshAuthorizationToken, handleCrashedErrors);

export default smartSolveApi;

/**
 * Does SYNC request
 * Dont use until you really need sync request
 *
 */
export const syncAPICall = (uri, params) => {
  const request = new XMLHttpRequest();
  request.open('POST', `${adminAppUrl}/apis/${uri}`, false); // `false` makes the request synchronous
  const state = store.getState();
  request.setRequestHeader('Authorization', `Bearer ${state.auth.token}`);
  request.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
  request.send(JSON.stringify(params));
  if (request.status === 200) {
    const parsedResponse = JSON.parse(request.responseText);

    if (!parsedResponse.hasError) {
      return parsedResponse;
    }

    const { errorMessage, errorNumber } = _.head(parsedResponse.errors);
    const sessionExpirationErrorCode = selectSessionExpirationErrorCode(state);
    const isSessionAlive = errorNumber !== sessionExpirationErrorCode;

    if (isSessionAlive) {
      return parsedResponse;
    }

    const { code, message } = getError(errorMessage, errorNumber);

    store.dispatch(expireToken(message, code));

    const prevActorCodeWithDomain = localStorage.getItem('prevActorCodeWithDomain');

    store.dispatch(waitLoginSuccess());

    store.dispatch(restoreTitle());

    return hasLogonUserChanged(prevActorCodeWithDomain) ? undefined : parsedResponse;
  }
  console.log(`syncAPICall() request is failed: ${request.response}`);
  return undefined;
};

export const getLicensedModule = (moduleName, formKey) => {
  const moduleLicensed = createSelector(
    [(state) => state.formDesigner[formKey].moduleLicensed],
    (moduleLicensed) => moduleLicensed || {}
  )(store.getState());

  if (_.isEmpty(moduleName)) {
    return "ss.isModuleLicensed(ssContext, '')";
  }

  const moduleLicensedProp = moduleLicensed ? moduleLicensed[moduleName] : undefined;

  try {
    if (!moduleLicensedProp || _.isEmpty(moduleName)) {
      const response = syncAPICall('moduleislicensed', {
        moduleName,
      });

      if (response?.hasError) {
        throw new Error('getLicensedModule() failed with error');
      }
      if (response) {
        store.dispatch(setModuleIsLicensed(moduleName, formKey, response));

        return response.moduleIsLicensed;
      }
    }

    return moduleLicensedProp.moduleIsLicensed;
  } catch (e) {
    throw new Error(`getLicensedModule() failed: ${e}`);
  }
};

export const getNumberingScheme = (numberingSchemeCode, orgList, prop, formKey) => {
  const numberingScheme = createSelector(
    [(state) => state.formDesigner[formKey]?.numberingScheme],
    (numberingScheme) => numberingScheme || {}
  )(store.getState());

  let numberingSchemeProp = prop ? numberingScheme[prop] : undefined;

  try {
    if (!numberingSchemeProp || _.isEmpty(numberingScheme)) {
      const response = syncAPICall('numberingScheme', {
        numberingSchemeCode,
        orgList,
      });

      if (response?.hasError) {
        throw new Error('getNumberingScheme() failed with error');
      }

      if (response) {
        store.dispatch(setNumberingScheme(formKey, response));
        if (prop) {
          numberingSchemeProp = response[prop];
        }
      }
    }
  } catch (e) {
    throw new Error(`getNumberingScheme() failed: ${e}`);
  }

  if (prop) {
    return numberingSchemeProp;
  }

  return numberingScheme;
};

export const getAddBusinessDays = (numberOfDaysToAdd, startDate, siteCode, formKey, identifier) => {
  const businessDaysDate = createSelector(
    [(state) => state.formDesigner[formKey]?.businessDaysDate],
    (businessDaysDate) => businessDaysDate || {}
  )(store.getState());

  const businessDaysDateProp = businessDaysDate ? businessDaysDate[identifier] : undefined;

  var utcDate = new Date(
    Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startDate.getHours(),
      startDate.getMinutes()
    )
  );

  try {
    if (!businessDaysDateProp || _.isEmpty(businessDaysDate)) {
      const response = syncAPICall('addbusinessdays', {
        numberOfDaysToAdd,
        startDate: utcDate,
        siteCode,
      });

      if (response?.hasError) {
        throw new Error('getAddBusinessDays() failed with error');
      }
      if (response) {
        store.dispatch(setDateAddBusinessDays(formKey, identifier, response.addBusinessDays));

        return response.addBusinessDays;
      }
    }

    return businessDaysDateProp;
  } catch (e) {
    throw new Error(`getAddBusinessDays() failed: ${e}`);
  }
};
